// ============================================================================
// PairsForm - Organisation Context
// ============================================================================
// Gère l'organisation active via les memberships du user.
// - Users multi-org : switch via memberships
// - SUPER_ADMIN Cecodev : peut aussi accéder à d'autres orgs via dropdown

import React, { createContext, useContext, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import type { Membership } from "@cecodev/shared";
import { membershipParDefaut } from "@cecodev/shared";

interface OrganisationContextType {
  // Membership actif (porté par le JWT)
  activeMembership: Membership | null;
  memberships: Membership[];
  // Org « visitée » en immersion (dérivée du JWT : non-null ⇔ impersonating).
  selectedOrganisationOverride: { id: string; nom: string; type: string } | null;
  // Entrer (org) / sortir (null) d'une immersion — round-trip serveur (re-signe le JWT).
  setSelectedOrganisationOverride: (org: { id: string; nom: string; type: string } | null) => Promise<void>;
  // Org effective = celle du JWT (immersion incluse). Le `?organisationId=` legacy
  // reste accepté côté serveur (= cette valeur), mais n'est plus nécessaire.
  effectiveOrganisationId: string | null;
  effectiveOrganisationNom: string | null;
  // Immersion en cours (on agit comme PILOTE d'une org non-membre)
  impersonating: boolean;
  // Switch d'org via membership
  switchMembership: (membershipId: string) => Promise<void>;
  isLoading: boolean;
}

const OrganisationContext = createContext<OrganisationContextType | undefined>(undefined);

export function OrganisationProvider({ children }: { children: React.ReactNode }) {
  const { user, switchOrg, immerseInOrg } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const memberships = user?.memberships || [];
  const activeMembership = memberships.find((m) => m.id === user?.activeMembershipId) || memberships[0] || null;

  // L'org effective EST celle du JWT — en immersion, le token est re-signé scopé
  // à l'org cible. Plus de calcul local : une seule source de vérité.
  const effectiveOrganisationId = user?.organisationId || null;
  const effectiveOrganisationNom = user?.organisationNom || null;
  const impersonating = !!user?.impersonating;

  // « Override » dérivé de l'état d'immersion (compat OrgSwitcher / OrgVisitBanner).
  const selectedOrganisationOverride = impersonating
    ? { id: user!.organisationId, nom: user!.organisationNom || "", type: user!.organisationType || "" }
    : null;

  // Entrer dans une org (immersion) ou en sortir (retour au membership maison).
  const setSelectedOrganisationOverride = useCallback(
    async (org: { id: string; nom: string; type: string } | null) => {
      setIsLoading(true);
      try {
        if (org) {
          await immerseInOrg(org.id);
        } else {
          // Sortie : retour à la maison (membership par défaut, ex. Cecodev) →
          // JWT réel ré-émis (SUPER_ADMIN), menu complet.
          const home = membershipParDefaut(memberships);
          if (home) await switchOrg(home.id);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [immerseInOrg, switchOrg, memberships],
  );

  const switchMembership = useCallback(async (membershipId: string) => {
    setIsLoading(true);
    try {
      await switchOrg(membershipId);
    } finally {
      setIsLoading(false);
    }
  }, [switchOrg]);

  return (
    <OrganisationContext.Provider
      value={{
        activeMembership,
        memberships,
        selectedOrganisationOverride,
        setSelectedOrganisationOverride,
        effectiveOrganisationId,
        effectiveOrganisationNom,
        impersonating,
        switchMembership,
        isLoading,
      }}
    >
      {children}
    </OrganisationContext.Provider>
  );
}

export function useOrganisation() {
  const context = useContext(OrganisationContext);
  if (context === undefined) {
    throw new Error("useOrganisation must be used within an OrganisationProvider");
  }
  return context;
}
